import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Progress, Space, Alert, notification, Tag } from 'antd';
import LogBox from 'components/LogBox/LogBox';
import { useTranslation } from 'react-i18next';
import AccreditationService from 'services/accreditationService';

const LOG_FILTER = {
  ALL: "all",
  SUCCESS: "success",
  ERROR: "error",
};

function ModalProgressCustomer({ dataFilter, visible, setVisible, data, setUpload, toggleUploadModal, setFileSelected, fetchData }) {
  const { t: translation } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [percent, setPercent] = useState(0);
  const [logs, setLogs] = useState([]);
  const [numberError, setNumberError] = useState(0);
  const [numberSuccess, setNumberSuccess] = useState(0);
  const [logFilter, setLogFilter] = useState(LOG_FILTER.ALL);

  const chunkArray = (array, size) => {
    const result = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  };

  useEffect(() => {
    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
  
    const processInBatches = async () => {
      if (visible && data.length > 0) {
        setIsLoading(true);
        setPercent(0);
        setLogs([]);
        setNumberError(0);
        setNumberSuccess(0);
        setLogFilter(LOG_FILTER.ALL);
  
        let processedCount = 0;
        const addLog = (entry) => setLogs((prev) => [{ id: Date.now() + Math.random(), ...entry }, ...prev]);

        const validItems = data
          .map((item, index) => {
            delete item[""];
            const errors = [];
  
            if (!item.customerRecordPlatenumber) errors.push("thiếu biển số xe");
            if (!item.customerRecordPhone) errors.push("thiếu số điện thoại");
            if (!item.customerRecordCheckExpiredDate) errors.push("thiếu ngày hết hạn");
  
            if (errors.length > 0) {
              addLog({
                message: `STT ${index + 1} ${errors.join(", ")}`,
                status: "error",
              });
              setNumberError((prev) => prev + 1);
              processedCount++;
              setPercent((processedCount / data.length) * 100);
              return null;
            }
  
            return { item, index };
          })
          .filter(Boolean);
  
        const chunks = chunkArray(validItems, 10);
  
        for (const chunk of chunks) {
          await Promise.all(
            chunk.map(({ item, index }) =>
              AccreditationService.createNewCustomer(item)
                .then((result) => {
                  if (result.statusCode === 200) {
                    addLog({
                      message: `Thành công ${item.customerRecordPlatenumber}`,
                      status: "success",
                    });
                    setNumberSuccess((prev) => prev + 1);
                  } else {
                    const msg =
                      result.error === "DUPLICATED_RECORD_IN_ONE_DAY" ||
                      result.error === "DUPLICATED_RECORD"
                        ? `STT ${index + 1} Khách hàng đã tồn tại ${item.customerRecordPlatenumber}`
                        : `Thất bại ${item.customerRecordPlatenumber}`;
  
                    addLog({
                      message: msg,
                      status: "error",
                    });
                    setNumberError((prev) => prev + 1);
                  }
                })
                .catch((error) => {
                  notification["error"]({
                    message: "",
                    description: translation("listCustomers.importTypeFailed"),
                  });
                  addLog({
                    message: `Thất bại ${item.customerRecordPlatenumber}: ${error.message}`,
                    status: "error",
                  });
                  setNumberError((prev) => prev + 1);
                })
                .finally(() => {
                  processedCount++;
                  setPercent((processedCount / data.length) * 100);
                })
            )
          );
  
          // Thêm thời gian chờ giữa các batch
          await sleep(100);
        }
  
        setIsLoading(false);
        setUpload(false);
        toggleUploadModal();
        setFileSelected(undefined);
      }
    };
  
    processInBatches();
  }, [visible, data, translation]);

  const handleCancel = () => {
    fetchData(dataFilter)
    setVisible(false);
  };

  const filteredLogs =
    logFilter === LOG_FILTER.ALL ? logs : logs.filter((log) => log.status === logFilter);

  return (
    <Modal
      visible={visible}
      onCancel={handleCancel}
      footer={null}
    >
      <div style={{ width: "100%" }} className='mt-4'>
        {isLoading ? (
          <p>{translation("progress.loading")}</p>
        ) : (
          <Space>
            <Alert message={translation("progress.messageNumberError", {
              number: numberError
            })} type={"error"} showIcon />
            <Alert message={translation("progress.messageNumberSuccess", {
              number: numberSuccess
            })} type={"success"} showIcon />
          </Space>
        )}
        <Progress percent={Math.round(percent)} size="default" />
        <Space className='mt-3'>
          <Tag.CheckableTag
            checked={logFilter === LOG_FILTER.ALL}
            onChange={() => setLogFilter(LOG_FILTER.ALL)}
          >
            {translation("progress.all", { defaultValue: "Tất cả" })} ({logs.length})
          </Tag.CheckableTag>
          <Tag.CheckableTag
            checked={logFilter === LOG_FILTER.SUCCESS}
            onChange={() => setLogFilter(LOG_FILTER.SUCCESS)}
          >
            {translation("progress.success", { defaultValue: "Thành công" })} ({numberSuccess})
          </Tag.CheckableTag>
          <Tag.CheckableTag
            checked={logFilter === LOG_FILTER.ERROR}
            onChange={() => setLogFilter(LOG_FILTER.ERROR)}
          >
            {translation("progress.error", { defaultValue: "Thất bại" })} ({numberError})
          </Tag.CheckableTag>
        </Space>
        <div className='mt-3'>
          <LogBox logs={filteredLogs} />
        </div>
      </div>
    </Modal>
  );
}

export default ModalProgressCustomer;
